import { CSSProperties, RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

// 配置が確定するまでの非表示スタイル。実測してから可視化することでちらつきを防ぐ
const HIDDEN_STYLE: CSSProperties = { position: 'fixed', top: 0, left: 0, visibility: 'hidden' };

// flip: false のとき、下に出したパネルの max-height をこの値未満には縮めない
const MIN_PANEL_HEIGHT = 120;

type Options = {
  // true（既定）: 下の余白が足りなければトリガー上に出す。
  // false: 常に下に出し、画面下端までに収まる max-height を付与する（中身はスクロール）。
  flip?: boolean;
};

// ドロップダウンパネルを body へ Portal し、トリガー基準で fixed 配置するためのフック。
// モーダルの overflow / 高さ / transform に影響されない。
// anchorRef: 位置の基準にする要素（トリガー、またはそれを内包するラッパー）。
// 使い方:
//   const rootRef = useRef<HTMLDivElement>(null);
//   const { panelRef, panelStyle } = useDropdownPlacement(open, rootRef);
//   {open && createPortal(
//     <div ref={panelRef} className="panel" style={panelStyle}>...</div>,
//     document.body,
//   )}
export const useDropdownPlacement = (
  open: boolean,
  anchorRef: RefObject<HTMLElement>,
  { flip = true }: Options = {},
) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelStyle, setPanelStyle] = useState<CSSProperties>(HIDDEN_STYLE);

  const update = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();

    if (!flip) {
      // 常に下。画面下端まで（最低 MIN_PANEL_HEIGHT）に収める
      const available = Math.max(MIN_PANEL_HEIGHT, Math.floor(window.innerHeight - rect.bottom - 16));
      setPanelStyle({
        position: 'fixed',
        top: rect.bottom + 8,
        left: rect.left,
        width: rect.width,
        maxHeight: available,
      });
      return;
    }

    const panelHeight = panelRef.current?.offsetHeight ?? 0;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    // 下に入りきらず、かつ上のほうが余白が大きいときだけ上向きにする
    const up = panelHeight > spaceBelow && spaceAbove > spaceBelow;

    setPanelStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      ...(up ? { bottom: window.innerHeight - rect.top + 8 } : { top: rect.bottom + 8 }),
    });
  }, [anchorRef, flip]);

  // 開いた直後に実測して配置（paint 前に確定させてちらつきを防ぐ）
  useLayoutEffect(() => {
    if (!open) {
      setPanelStyle(HIDDEN_STYLE);
      return;
    }
    update();
  }, [open, update]);

  // 開いている間はスクロール・リサイズに追従する
  useEffect(() => {
    if (!open) return;
    const onChange = () => update();
    window.addEventListener('resize', onChange);
    // 祖先のスクロールでも位置が変わるため capture で拾う
    window.addEventListener('scroll', onChange, true);
    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('scroll', onChange, true);
    };
  }, [open, update]);

  return { panelRef, panelStyle };
};
